/**
 * Accessible mode (Q-2214, lane L7): the WCAG 2.0 AA mode Andrew asked for.
 *
 * STORE. `pref:a11y.mode.v1` in localStorage, synced to Supabase
 * `user_preferences` by `useUserPreference` (see `useA11yMode`), so the choice
 * follows the user between devices. The value is the JSON string "on" | "off".
 *
 * OVERRIDE. `?a11y=1` / `?a11y=0` on the page URL beats the stored value for
 * this page load only and is NEVER written back (the tester flag).
 *
 * NO FLASH. `a11yBootScript` runs inline in <head>, before first paint, and sets
 * `data-a11y-mode="on"` on <html>. It must stay self-contained ES5 (it is a
 * string, not imported code) and must never throw.
 */

export type A11yMode = 'on' | 'off';

export const A11Y_MODE_PREF_KEY = 'a11y.mode.v1';
/** useUserPreference's localStorage key for the preference. */
export const A11Y_MODE_STORAGE_KEY = `pref:${A11Y_MODE_PREF_KEY}`;
export const A11Y_MODE_ATTRIBUTE = 'data-a11y-mode';

function parseMode(raw: string | null | undefined): A11yMode | null {
  if (raw === 'on' || raw === 'off') return raw;
  return null;
}

/** URL flag first (1/0), then the stored value, else off. Never throws. */
export function resolveA11yMode({ search, stored }: { search: string; stored: string | null }): A11yMode {
  let flag: string | null = null;
  try {
    flag = new URLSearchParams(search).get('a11y');
  } catch {
    flag = null;
  }
  if (flag === '1') return 'on';
  if (flag === '0') return 'off';
  return parseMode(stored) ?? 'off';
}

/** Mode off removes the attribute so the default look has no trace of it. */
export function applyA11yMode(doc: Document, mode: A11yMode): void {
  if (mode === 'on') doc.documentElement.setAttribute(A11Y_MODE_ATTRIBUTE, 'on');
  else doc.documentElement.removeAttribute(A11Y_MODE_ATTRIBUTE);
}

/** Parse a useUserPreference localStorage value ("\"on\"") tolerantly. */
export function parseStoredMode(raw: string | null): string | null {
  if (raw === null) return null;
  try {
    const v = JSON.parse(raw);
    return typeof v === 'string' ? v : null;
  } catch {
    return raw;
  }
}

export const a11yBootScript = `(function(){try{var m=null;var f=new URLSearchParams(window.location.search).get('a11y');if(f==='1'){m='on'}else if(f==='0'){m='off'}else{var r=null;try{r=window.localStorage.getItem(${JSON.stringify(A11Y_MODE_STORAGE_KEY)})}catch(e){}if(r!==null){try{r=JSON.parse(r)}catch(e){}}if(r==='on'||r==='off'){m=r}}if(m==='on'){document.documentElement.setAttribute(${JSON.stringify(A11Y_MODE_ATTRIBUTE)},'on')}else{document.documentElement.removeAttribute(${JSON.stringify(A11Y_MODE_ATTRIBUTE)})}}catch(e){}})();`;
